import { useState } from "react";
import { useToasts } from "../Components/Toasts";
import API from "../Services/API";
import Model from "./Model";
import { toQueryString } from "../Services/helpers";
import State from "./State";

const useReport = (dateRange, interval) => {
    return Model({
        stateKey: ['admin', 'report', dateRange, interval],
        alwaysRefresh: true,
        fetchData: () => API.get(`admin/reports?dateRange=${dateRange}&interval=${interval}`),
        returnDataKey: 'report',
        defaultLoadingError: 'Sorry, there was an error loading report.',
    })
}

const useNotifications = () => {
    return Model({
        stateKey: ['admin', 'notifications'],
        alwaysRefresh: true,
        fetchData: () => API.get('admin/notifications'),
        returnDataKey: 'notifications',
        defaultLoadingError: 'Sorry, there was an error loading notifications.',
        registerAdditionalMethods: (opts) => {
            return {
                markAllSeen: () => {
                    return opts.wrapUpdateCall(
                        () => API.post('notifications/markAllSeen', { adminJournal: true }),
                        null,
                        true,
                    );
                },
            }
        }

    })
}

const useBusinesses = (searchData) => {
    const searchStr = toQueryString(searchData);

    return Model({
        stateKey: ['admin', 'businesses', searchStr],
        alwaysRefresh: true,
        fetchData: () => API.get(`admin/businesses?${searchStr}`),
        returnDataKey: 'businesses',
        defaultLoadingError: 'Sorry, there was an error loading businesses.',
    })
}

const useBusiness = (businessId, alwaysRefresh = true) => {
    return Model({
        alwaysRefresh,
        stateKey: ['admin', 'business', businessId],
        fetchData: () => API.get(`admin/businesses/${businessId}`),
        returnDataKey: 'business',
        defaultLoadingError: 'Sorry, there was an error loading the business.',
        registerAdditionalMethods: (opts) => {
            return {
                applyConcession: (concession, validUntil) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/admin/concession/apply', {
                            businessId,
                            concession,
                            validUntil: validUntil || null,
                        }),
                        null,
                        true
                    );
                },
                removeConcession: () => {
                    return opts.wrapUpdateCall(
                        () => API.post('/admin/concession/remove', {
                            businessId,
                        }),
                        null,
                        true
                    );
                },
                addPlanCredits: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/admin/credits/add', {
                            businessId,
                            ...data,
                        }),
                        null,
                        true
                    );
                },
            }
        }
    })
}

const useBalanceAdjustments = (args) => {
    const ret = Model({
        assumeLoading: true,
        returnDataKey: 'data',
        stateKey: null,
        fetchData: () => API.get(composeAdjustmentsUrl(args)),
    });

    return {
        ...ret,
        summary: ret?.data?.summary,
        adjustments: ret?.data?.adjustments,
        expected: ret?.data?.expected,
    }
}

const composeAdjustmentsUrl = (args) => {
    const query = {
        year: args.year ?? '',
        month: args.month ?? '',
        includeTestAccounts: (args.includeTestAccounts ?? false) ? 1 : 0,
        includeNonSoapyRevenue: (args.includeNonSoapyRevenue ?? false) ? 1 : 0,
        includeAnyChangePence: (args.includeAnyChangePence ?? false) ? 1 : 0,
    };
    const download = args.download ?? false;

    const path = `admin/adjustments/monthly${download ? '/download' : ''}`
    const baseUrl = download ? API.domain : '/';

    return `${baseUrl}${path}?${new URLSearchParams(query).toString()}`;
}

const useReferralSummary = (args) => {
    return Model({
        stateKey: ['admin', 'referralSummary', args],
        alwaysRefresh: true,
        fetchData: () => API.get(`admin/adjustments/referrals?year=${args.year}&month=${args.month}&includeTestAccounts=${args.includeTestAccounts ? 1 : 0}`),
        returnDataKey: 'referralSummary',
    })
}

const useSendBusinessAdHocNotification = () => {
    const [isSending, setIsSending] = useState(false);
    const {showSuccess, showError} = useToasts();

    const send = (args) => {
        return new Promise((resolve, reject) => {
            setIsSending(true);
            API.post('admin/sendAdHocNotification', args).then((res) => {
                setIsSending(false);
                showSuccess('Notification sent');
                resolve(res);
            }).catch((err) => {
                showError(API.parseErrorResponse(err));
                setIsSending(false);
                reject(err);
            });
        });
    }

    return { isSending, send };
}

const useExperiments = (model, days) => {
    return Model({
        stateKey: ['admin', 'experiments', model, days],
        alwaysRefresh: true,
        fetchData: () => API.get(`/admin/experiments/${model.toLowerCase()}?days=${days}`),
        returnDataKey: 'experiments',
        defaultLoadingError: 'Sorry, there was an error loading experiment data.',
    })
}

const useConversations = (searchData) => {
    searchData.include = 'user,business,nudge,latestMessage,feedback';
    let searchStr = toQueryString(searchData);

    return Model({
        alwaysRefresh: true,
        stateKey: ['admin', 'conversations', searchStr],
        fetchData: () => API.get(`/assistant/admin/conversations?${searchStr}`),
        saveAdditionalData: (conversations) => {
            conversations.forEach(conversation => {
                State.putIfNotExists(['admin', 'conversation', conversation.id], conversation)
            })
        },
        returnDataKey: 'conversations',
        defaultLoadingError: 'Sorry, there was an error loading conversations.',
    })
}

const useConversation = (conversationId) => {
    return Model({
        alwaysRefresh: true,
        stateKey: ['admin', 'conversation', conversationId],
        fetchData: () => API.get(`/assistant/admin/conversations/${conversationId}`),
        subscribeToUpdates: {
            entity: {
                entityModel: 'Assistant/Conversation',
                entityId: conversationId,
            }
        },
        returnDataKey: 'conversation',
        defaultLoadingError: 'Sorry, there was an error loading the conversation.',
        registerAdditionalMethods: (opts) => {
            return {
                addAdminMessage: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/messages`, data),
                        null,
                        true
                    );
                },
                flagForHumanAttention: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/flag-for-human-attention`),
                        null,
                        true
                    );
                },
                removeHumanAttention: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/remove-human-attention`),
                        null,
                        true
                    );
                },
                changeAssistantActiveState: (active) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/change-assistant-active-state`, { active }),
                        null,
                        true
                    );
                },
                markAsSeenByAdmin: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/seen`),
                        null,
                        true
                    );
                },
                archive: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/archive`),
                        null,
                        true
                    );
                },
                star: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/star`),
                        null,
                        true
                    );
                },
                unstar: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/unstar`),
                        null,
                        true
                    );
                },
                protect: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/protect`),
                        null,
                        true
                    );
                },
                unprotect: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/unprotect`),
                        null,
                        true
                    );
                },
                nudgeAssistant: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/nudge`),
                        null,
                        true
                    );
                },
            }
        }
    })
}

const usePreviousConversations = (userId, businessId) => {
    return Model({
        block: !(userId && businessId),
        alwaysRefresh: true,
        stateKey: ['admin', 'previousConversations', userId, businessId],
        fetchData: () => API.get(`/assistant/admin/conversations/previous?userId=${userId}&businessId=${businessId}`),
        returnDataKey: 'previousConversations',
        defaultLoadingError: 'Sorry, there was an error loading previous conversations.',
    })
}

const useAssets = (limit, offset) => {
    return Model({
        alwaysRefresh: true,
        stateKey: ['admin', 'assets', limit, offset],
        fetchData: () => API.get('/assets/all', { limit, offset }),
        returnDataKey: 'assets',
        defaultLoadingError: 'Sorry, there was an error loading assets.',
    })
}

const useConcessions = () => {
    return Model({
        stateKey: ['admin', 'concessions'],
        fetchData: () => API.get('admin/concessions'),
        returnDataKey: 'concessions',
        defaultLoadingError: 'Sorry, there was an error loading concessions.',
    })
}

const useLessons = (searchData, alwaysRefresh = false) => {
    const searchStr = toQueryString(searchData);
    return Model({
        stateKey: ['admin', 'lessons', searchStr],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/assistant/admin/lessons?${searchStr}`),
        returnDataKey: 'lessons',
        defaultLoadingError: 'Sorry, there was an error loading lessons.',
        registerAdditionalMethods: (opts) => {
            return {
                updateLessonStatus: (lessonId, status) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/lessons/${lessonId}/status`, { status }),
                        null,
                        true,
                        false,
                    );
                },
            }
        }
    })
}

const usePreviousNudges = () => {
    return Model({
        stateKey: ['admin', 'previousNudges'],
        fetchData: () => API.get('admin/nudges/previous'),
        returnDataKey: 'previousNudges',
        defaultLoadingError: 'Sorry, there was an error loading previous nudges.',
    })
}

const useFeedback = (searchData, alwaysRefresh = false) => {
    const searchStr = toQueryString(searchData);
    return Model({
        stateKey: ['admin', 'feedback', searchStr],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/assistant/admin/feedback?${searchStr}`),
        returnDataKey: 'feedback',
        defaultLoadingError: 'Sorry, there was an error loading feedback.',
    })
}

const feedbackTypes = {
    NUDGE: 'Nudge',
    BUG_REPORT: 'Bug Report',
    FEATURE_REQUEST: 'Feature Request',
    FEEDBACK: 'Feedback',
    OTHER: 'Other',
}

const feedbackTypeThemes = {
    [feedbackTypes.NUDGE]: 'warning',
    [feedbackTypes.BUG_REPORT]: 'danger',
    [feedbackTypes.FEATURE_REQUEST]: 'info',
    [feedbackTypes.FEEDBACK]: 'brand',
    [feedbackTypes.OTHER]: 'secondary',
}

const levels = {
    EARLY_ACCESS: 'Early Access',
    READ_ONLY: 'Read Only',
    ADMIN: 'Admin',
    SUPER: 'Super',
}

export {
    useReport,
    useNotifications,
    useBusinesses,
    useBusiness,
    useBalanceAdjustments,
    useReferralSummary,
    useSendBusinessAdHocNotification,
    useExperiments,
    useConversations,
    useConversation,
    usePreviousConversations,
    levels,
    useAssets,
    useConcessions,
    useLessons,
    usePreviousNudges,
    useFeedback,
    feedbackTypes,
    feedbackTypeThemes,
};
